import React, { useMemo, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import TableContainer from "./TableContainer";
import PatientLoader from "../../../common/PatientLoader";
import "./AllClinicsDataTableStyles.css";
import { useNavigate, useParams } from "react-router-dom";
import { fetchTestHealthCareGroupsLogic } from "../../../../store/features/ops/opsSlice";
import RiskPill from "../../../common/RiskPill";

const AllClinicsDataTable = () => {
  const navigate = useNavigate();
  const { opsLoading, healthcareGroups, allClinics } = useSelector(
    (state) => state.ops
  );
  const [loading, setLoading] = useState(true);
  const [testClinics, setTestClincs] = useState([]);
  const [testStatusLoading, setTestStatusLoading] = useState(false);
  function getTestClinics() {
    fetchTestHealthCareGroupsLogic({ is_test_account: 1, search: "" })
      .then((res) => {
        setTestClincs(res.data);
        setLoading(false);
      })
      .catch((e) => {
        setLoading(false);
      });
  }
  const handleTestStatusToggle = (id) => {
    setTestStatusLoading(true);
    console.log(id); //TODO: Call test status toggle API here.
    getTestClinics();
    setTestStatusLoading(false);
  };

  const [testOnly, setTestOnly] = useState(params?.type);
  const params = useParams();
  useEffect(() => {
    if (params?.type === "test") {
      setTestOnly(true);
    } else {
      setTestOnly(false);
    }
  }, [params]);

  useEffect(() => {
    getTestClinics();
  }, []);

  const getGroupName = (id) => {
    let thisGroup;
    if (healthcareGroups) {
      thisGroup = healthcareGroups.find((x) => x.id === id);
      if (thisGroup) return thisGroup.name;
    }

    if (testClinics) {
      thisGroup = testClinics.find((x) => x.id === id);
      if (thisGroup) return thisGroup.name;
    }

    return null;
  };

  const getStatusToDisplay = (status, id) => {
    let thisGroup;
    thisGroup = healthcareGroups.find((x) => x.id === id);

    if (!thisGroup) return "Demo";
    if (status) return "Active";
    else return "Inactive";
  };

  const getOpenInvoices = (id) => {
    let thisGroup = healthcareGroups.find((x) => x.id === id);
    if (thisGroup === undefined) return 0;
    const invoicingHistory = thisGroup?.invoicing_history;
    if (!invoicingHistory) return 0;
    let temp = [];
    temp = invoicingHistory.filter((item) => item?.status === "OPEN");
    return parseInt(temp.length);
  };

  let renderColumns = [
    {
      Header: "Name",
      accessor: "name",
      Cell: ({ cell }) => {
        return (
          <span style={{ textTransform: "capitalize" }}>{cell.value}</span>
        );
      },
    },
    {
      Header: "Created",
      accessor: "created",
      sortType: (a, b) => {
        if (a === null || a === undefined || a === "" || a === "Not Available")
          return 1; // place null values at the bottom
        if (b === null || b === undefined || b === "" || b === "Not Available")
          return -1;
        return new Date(b?.values?.created) - new Date(a?.values?.created);
      },

      Cell: ({ cell }) => {
        return (
          <span style={{ textTransform: "capitalize" }}>
            {cell.value
              ? moment(cell.value).format("MMM Do, YYYY")
              : "Not Available"}
          </span>
        );
      },
    },
    {
      Header: "Clinic Group",
      accessor: "clinic_group",
      Cell: ({ cell }) => {
        return (
          <span style={{ textTransform: "capitalize" }}>
            {getGroupName(cell.value) || "Not Specified"}
          </span>
        );
      },
    },
    {
      Header: "Contract Status",
      accessor: (d) => [d.invoicing_metadata, d.clinic_group],
      Cell: ({ cell }) => {
        const value = cell.value[0]
          ? getStatusToDisplay(cell.value[0], cell.value[1])
          : "Not Specified";
        return <span style={{ textTransform: "capitalize" }}>{value}</span>;
      },
    },
    {
      Header: "Contract Termination",
      accessor: "invoicing_metadata",
      Cell: ({ cell }) => {
        let date = "Not Specified";
        if (cell.value && cell.value.length > 0) {
          date = cell?.value[0]["contract_termination_date"];
        }
        return <span style={{ textTransform: "capitalize" }}>{date}</span>;
      },
    },
    {
      Header: "Providers",
      accessor: (d) => d.invoicing_metadata,
      disableSortBy: true,
      Cell: ({ cell }) => {
        return (
          <span>
            {cell.value?.number_of_providers_to_charge_monthly_fees ||
              "Not Specified"}
          </span>
        );
      },
    },
    {
      Header: "Invoices Pending Action",
      accessor: (d) => d.id,
      Cell: ({ cell }) => {
        return (
          <span style={{ textTransform: "capitalize" }}>
            {getOpenInvoices(cell.value)}
          </span>
        );
      },
    },
    {
      Header: "Actions",
      accessor: "id",
      disableSortBy: true,
      Cell: ({ cell }) => {
        return (
          <button
            onClick={() => navigate(`/ops/clinic/${cell.value}`)}
            className="primary-btn-small"
          >
            View
          </button>
        );
      },
    },
  ];

  let renderTestColumns = [
    {
      Header: "Name",
      accessor: "name",
      Cell: ({ cell }) => {
        return (
          <span style={{ textTransform: "capitalize" }}>{cell.value}</span>
        );
      },
    },
    {
      Header: "Created",
      accessor: "created",
      sortType: (a, b) => {
        if (a === null || a === undefined || a === "" || a === "Not Available")
          return 1; // place null values at the bottom
        if (b === null || b === undefined || b === "" || b === "Not Available")
          return -1;
        return new Date(b?.values?.created) - new Date(a?.values?.created);
      },

      Cell: ({ cell }) => {
        return (
          <span style={{ textTransform: "capitalize" }}>
            {cell.value
              ? moment(cell.value).format("MMM Do, YYYY")
              : "Not Available"}
          </span>
        );
      },
    },
    {
      Header: "Type",
      accessor: "type",
      Cell: ({ cell }) => {
        return (
          <RiskPill
            icon_type="user"
            styles={{ width: "100%", gap: "5px" }}
            type={cell?.value?.toLowerCase() || "not specified"}
            show_icon={true}
          />
        );
      },
    },
    {
      Header: "Sales Representative",
      accessor: "rose_sales_team_member",
      Cell: ({ cell }) => {
        return (
          <span style={{ textTransform: "capitalize" }}>
            {cell.value || "Not Specified"}
          </span>
        );
      },
    },
    {
      Header: "Invoices Pending Action",
      accessor: "invoicing_history",
      disableSortBy: true,
      Cell: ({ cell }) => {
        let pendingInvoices = 0;
        pendingInvoices = cell.value?.length > 0 && getOpenInvoices(cell.value);
        if (pendingInvoices === false) {
          pendingInvoices = 0;
        }
        return (
          <span style={{ color: pendingInvoices > 0 ? "red" : "green" }}>
            {pendingInvoices}
          </span>
        );
      },
    },
    {
      Header: "Toggle Test Status",
      accessor: (d) => [d.id, d.is_test_account],
      disableSortBy: true,
      Cell: ({ cell }) => {
        return (
          <label className="switch">
            <input
              disabled={testStatusLoading}
              onChange={() => {
                handleTestStatusToggle(cell.value[0]);
              }}
              checked={cell.value[1]}
              type="checkbox"
            />
            <span className="slider round"></span>
          </label>
        );
      },
    },
    {
      Header: "Actions",
      accessor: "id",
      disableSortBy: true,
      Cell: ({ cell }) => {
        return (
          <button
            onClick={() => navigate(`/ops/${cell.value}`)}
            className="primary-btn-small"
          >
            View
          </button>
        );
      },
    },
  ];

  const columns = useMemo(
    () => (testOnly ? renderTestColumns : renderColumns),
    [opsLoading, loading]
  );

  return (
    <>
      {opsLoading || loading ? (
        <PatientLoader />
      ) : (
        <TableContainer
          columns={columns}
          data={testOnly ? testClinics : allClinics}
        />
      )}
    </>
  );
};

export default AllClinicsDataTable;
